// src/utils/pricing.js
// ─────────────────────────────────────────────────────────────────────────────
// Shared GBP / USD pricing rules.
//
//   product.pricingMatrix     → £ price per combination (key "colour|length|texture|type")
//   product.pricingMatrixUSD  → $ price per combination (same keys, entered by the client)
//   settings/pricing (Firestore) → { usEnabled, usShippingUSD, usFreeShippingOverUSD }
//
// A combination is sold in the US only if it has BOTH a £ price and a $ price.
//
// IMPORTANT: the checkout server must use this same file, so the price shown
// on the site is exactly what Stripe charges. It has no imports, so it can be
// copied to the server as-is (swap `export` for module.exports if the server
// uses CommonJS).
// ─────────────────────────────────────────────────────────────────────────────

export const SUPPORTED_CURRENCIES = ['GBP', 'USD'];

export const DEFAULT_PRICING_SETTINGS = {
    usEnabled: false,            // master switch: show USD + accept US addresses
    usShippingUSD: null,         // flat US shipping fee in dollars
    usFreeShippingOverUSD: null, // optional: free US shipping at or above this subtotal
};

export const normaliseSettings = (settings) => ({ ...DEFAULT_PRICING_SETTINGS, ...(settings || {}) });

const isBlank = (v) => v === undefined || v === null || v === '';
const toPositiveNumber = (v) => {
    if (isBlank(v)) return null;
    const n = Number(v);
    return !isNaN(n) && n > 0 ? n : null;
};

// Is the US storefront switched on and configured?
export const isUsStoreReady = (settings) => {
    const s = normaliseSettings(settings);
    const fee = Number(s.usShippingUSD);
    return Boolean(s.usEnabled) && !isBlank(s.usShippingUSD) && !isNaN(fee) && fee >= 0;
};

// The £ price table for a product.
// Older products (saved before the pricing table existed) keep prices in
// `colors[].lengths[]`, `variants[]` or just `price`. Those are converted here
// the same way the product page does, so the server can price them too.
// Legacy products are GBP-only until they're re-saved in the admin with $ prices.
export const getGbpMatrix = (product) => {
    const stored = product?.pricingMatrix;
    if (stored && Object.keys(stored).length > 0) return stored;

    const out = {};
    if (Array.isArray(product?.colors) && product.colors[0] && typeof product.colors[0] === 'object') {
        product.colors.forEach(c => c?.lengths?.forEach(l => { out[`${c.name}|${l.length}||`] = l.price; }));
    } else if (Array.isArray(product?.variants)) {
        product.variants.forEach(v => { out[`|${v.length}||`] = v.price; });
    }
    // Products with no options at all are added with the key "|||"
    if (out['|||'] === undefined && toPositiveNumber(product?.price) !== null) out['|||'] = product.price;
    return out;
};

// Price of one combination in the given currency, or null if it isn't sold in that currency.
export const getVariantPrice = (product, key, currency) => {
    const gbp = toPositiveNumber(getGbpMatrix(product)[key]);
    if (gbp === null) return null; // combination doesn't exist

    if (currency === 'GBP') return gbp;
    if (currency === 'USD') return toPositiveNumber(product?.pricingMatrixUSD?.[key]);
    return null;
};

// "From £X / From $X" price for product cards. Null = not sold in that currency.
export const getFromPrice = (product, currency) => {
    const prices = Object.keys(getGbpMatrix(product))
        .map(k => getVariantPrice(product, k, currency))
        .filter(p => p !== null);
    return prices.length ? Math.min(...prices) : null;
};

// Can this product be bought at all in the given currency?
export const isAvailableIn = (product, currency) => getFromPrice(product, currency) !== null;

// US shipping for a given USD subtotal.
export const getUsShippingUSD = (subtotalUSD, settings) => {
    const s = normaliseSettings(settings);
    const threshold = Number(s.usFreeShippingOverUSD);
    if (threshold > 0 && subtotalUSD >= threshold) return 0;
    return Number(s.usShippingUSD) || 0;
};

// £120 / £120.50 / $165: whole numbers drop the ".00".
export const formatPrice = (amount, currency = 'GBP') => {
    if (amount === null || amount === undefined || isNaN(Number(amount))) return '';
    const n = Number(amount);
    return new Intl.NumberFormat(currency === 'USD' ? 'en-US' : 'en-GB', {
        style: 'currency',
        currency,
        minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
        maximumFractionDigits: 2,
    }).format(n);
};